/*

mount()
destroy()

pageIn()
pageOut()

view()
track()


*/

import { Observe, ObserveConfig, Track } from "@/modules/_";
import { Resize } from "@/lib/subs";

/** -- <stores> */
const destroy: Array<{ fn: () => void; element?: HTMLElement }> = [];
const mount: Array<() => void> = [];

// Track current component context
let currentComponentElement: HTMLElement | null = null;

export function setCurrentComponentElement(element: HTMLElement) {
  currentComponentElement = element;
}

export function clearCurrentComponentElement() {
  currentComponentElement = null;
}

/** -- <lifecycle> */
export function onMount(fn: () => void) {
  mount.push(fn);
}

export function onDestroy(fn: () => void) {
  destroy.push({ fn, element: currentComponentElement || undefined });
}

export function runDestroy(scopeContainer?: Element | HTMLElement) {
  if (scopeContainer) {
    // Only destroy components within the scope container
    const toDestroy = destroy.filter(({ element }) => {
      if (!element) return false; // Skip global components without element context
      return scopeContainer.contains(element);
    });

    toDestroy.forEach(({ fn }) => fn());

    // Remove destroyed items from the array
    destroy.splice(
      0,
      destroy.length,
      ...destroy.filter(({ element }) => {
        if (!element) return true; // Keep global components
        return !scopeContainer.contains(element);
      })
    );
  } else {
    // Destroy all (original behavior for backwards compatibility)
    destroy.forEach(({ fn }) => fn());
    destroy.length = 0;
  }
  //   console.log("destroy -> []", destroy);
}

export function runMount() {
  mount.forEach((fn) => fn());
  mount.length = 0;
  //   console.log("mount -> []", mount);
}

/** -- <animation> */
const pageOut: Array<{ fn: () => Promise<void>; element?: HTMLElement }> = [];
const pageIn: Array<() => Promise<void>> = [];

export function onPageOut(
  fn: () => Promise<void>,
  { element }: { element?: HTMLElement } = {}
) {
  const contextElement = element || currentComponentElement;
  if (contextElement) {
    pageOut.push({
      fn: async () => {
        const rect = contextElement.getBoundingClientRect();
        const isCurrentlyVisible = rect.top < Resize.height && rect.bottom > 0;
        return isCurrentlyVisible ? await fn() : Promise.resolve();
      },
      element: contextElement,
    });
  } else {
    pageOut.push({ fn, element: undefined });
  }
}

export async function runPageOut(scopeContainer?: Element | HTMLElement) {
  if (scopeContainer) {
    // Only run pageOut for components within the scope container
    const toRun = pageOut.filter(({ element }) => {
      if (!element) return false; // Skip global components without element context
      return scopeContainer.contains(element);
    });

    await Promise.allSettled(toRun.map(({ fn }) => fn()));

    // Remove processed items from the array
    pageOut.splice(
      0,
      pageOut.length,
      ...pageOut.filter(({ element }) => {
        if (!element) return true; // Keep global components
        return !scopeContainer.contains(element);
      })
    );
  } else {
    // Run all (original behavior for backwards compatibility)
    await Promise.allSettled(pageOut.map(({ fn }) => fn()));
    pageOut.length = 0;
  }
  //   console.log("page -> []", page);
}

export function onPageIn(fn: () => Promise<void>) {
  pageIn.push(fn);
}

export async function runPageIn() {
  await Promise.allSettled(pageIn.map((fn) => fn()));
  pageIn.length = 0;
}

export function onView(
  element: HTMLElement,
  { root, rootMargin, threshold, autoStart, once, callback }: ObserveConfig
) {
  const observer = new Observe(element, {
    root,
    rootMargin,
    threshold,
    autoStart,
    once,
    callback,
  });

  onDestroy(() => {
    observer.destroy();
  });

  return observer;
}

export function onTrack(
  element: HTMLElement,
  config: {
    bounds?: [number, number];
    top?: "top" | "center" | "bottom";
    bottom?: "top" | "center" | "bottom";
    callback?: (value: number) => void;
  } = {}
) {
  const track = new Track(element, config);

  onDestroy(() => {
    track.destroy();
  });

  return track;
}
